import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiPath from "../../utils/apiPath";

// Helper for consistent error extraction
const extractError = (err, rejectWithValue) => {
  if (err.response?.data) {
    return rejectWithValue(err.response.data.message || err.response.data);
  }
  return rejectWithValue(err.message || "Something went wrong");
};

// Async Thunks
export const fetchFields = createAsyncThunk(
  "field/fetchAll",
  async (blockId, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/fields?block_id=${blockId}`
      );
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const createField = createAsyncThunk(
  "field/create",
  async (fieldData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post(`/api/v1.0/fields/create`, fieldData);
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const updateField = createAsyncThunk(
  "field/update",
  async ({ id, fieldData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.put(`/api/v1.0/fields/${id}`, fieldData);
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const fieldDetail = createAsyncThunk(
  "field/detail",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(`/api/v1.0/fields/${id}`);
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const deleteField = createAsyncThunk(
  "field/delete",
  async (id, { rejectWithValue }) => {
    try {
      await apiPath.delete(`/api/v1.0/fields/${id}`);
      return id;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const fieldLists = createAsyncThunk(
  "field/list",
  async (blockId, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/fields/lists?block_id=${blockId}&status=active`
      );
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const fetchRelationalFieldLists = createAsyncThunk(
  "relationalfield/list",
  async (blockId, { rejectWithValue }) => {
    try {
      const response = await apiPath.get(
        `/api/v1.0/fields/lists?block_id=${blockId}&status=active`
      );
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

export const updateSingleFieldData = createAsyncThunk(
  "single-field/update",
  async ({ id, fieldData }, { rejectWithValue }) => {
    try {
      const response = await apiPath.patch(`/api/v1.0/fields/${id}`, fieldData);
      return response.data;
    } catch (err) {
      return extractError(err, rejectWithValue);
    }
  }
);

/* save ordering */
export const saveFieldOrdering = createAsyncThunk(
  'field/ordering',
  async (fieldOrderingData, { rejectWithValue }) => {
    try {
      const response = await apiPath.post(`/api/v1.0/fields/ordering`, fieldOrderingData);
      return response.data;
    } catch (err) {
      if (err.response && err.response.data) {
        return rejectWithValue(err.response.data);
      }
      return rejectWithValue(err.message);
    }
  }
);


// Slice
const fieldSlice = createSlice({
  name: "field",
  initialState: {
    fields: [],
    allFieldsList: [],
    relationalFieldLists: [],
    blockServices: [],
    fieldDetail: null,
    loading: false,
    error: null
  },
  reducers: {
    resetFieldError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // FETCH FIELDS
      .addCase(fetchFields.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFields.fulfilled, (state, action) => {
        state.loading = false;
        state.fields = action.payload.fields || [];
        state.blockServices = action.payload.blockServices || [];
      })
      .addCase(fetchFields.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error.message;
      })

      // CREATE FIELD
      .addCase(createField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createField.fulfilled, (state, action) => {
        state.fields.push(action.payload);
        state.loading = false;
        state.error = null;
      })
      .addCase(createField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // UPDATE FIELD
      .addCase(updateField.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateField.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.fields.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) state.fields[index] = action.payload;
      })
      .addCase(updateField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // DELETE FIELD
      .addCase(deleteField.fulfilled, (state, action) => {
        state.fields = state.fields.filter((f) => f.id !== action.payload);
        state.loading = false;
      })
      .addCase(deleteField.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FIELD DETAIL
      .addCase(fieldDetail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fieldDetail.fulfilled, (state, action) => {
        state.loading = false;
        state.fieldDetail = action.payload.details;
      })
      .addCase(fieldDetail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // FIELD LISTS
      .addCase(fieldLists.pending, (state) => {
        state.loading = true;
      })
      .addCase(fieldLists.fulfilled, (state, action) => {
        state.loading = false;
        state.allFieldsList = action.payload || [];
      })
      .addCase(fieldLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // RELATIONAL FIELD LISTS
      .addCase(fetchRelationalFieldLists.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchRelationalFieldLists.fulfilled, (state, action) => {
        state.loading = false;
        state.relationalFieldLists = action.payload || [];
      })
      .addCase(fetchRelationalFieldLists.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // SINGLE FIELD PATCH
      .addCase(updateSingleFieldData.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.fields.findIndex((f) => f.id === action.payload.id);
        if (index !== -1) state.fields[index] = action.payload;
      })
      .addCase(updateSingleFieldData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      /* Field Ordering */
      .addCase(saveFieldOrdering.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveFieldOrdering.fulfilled, (state,action) => {
        state.loading = false;
        state.error = null;
        state.fields = action.payload.fields
      })
      .addCase(saveFieldOrdering.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
  }
});

export const { resetFieldError } = fieldSlice.actions;
export default fieldSlice.reducer;  